import React, { useState, useEffect } from 'react';
import {
  FaUser, FaBuilding, FaBriefcase, FaMoneyBillWave,
  FaCalculator, FaSave, FaArrowLeft, FaPercentage, FaShieldAlt
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import './CreateSalary.css';

const CreateSalary = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user }  = useAuth();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const {
    employee, collegeName, selectedYear, selectedMonth, daysInMonth,
    presentDays, absentDays: passedAbsent,
    monthlySalary: empMonthlySalary, pfDeduction: empPf, esiDeduction: empEsi,
    netDailySalary: empDailySalary,
    existingOtherDeduction, existingAdvanceAdjust,
  } = location.state || {};

  // Live employee data + past salary records for recover calculation
  const [liveEmp, setLiveEmp] = useState(null);
  const [recoverSecurity, setRecoverSecurity] = useState(0); // sum of past securityDeduction

  useEffect(() => {
    if (!employee?._id) return;
    // fetch live employee
    api.get('/employees', { params: { limit: 500 } })
      .then(r => {
        const found = (r.data.data || []).find(e => String(e._id) === String(employee._id));
        if (found) setLiveEmp(found);
      }).catch(() => {});
    // fetch all paid salaries to sum up already-recovered security
    api.get('/salary', { params: { employee: employee._id, limit: 500 } })
      .then(r => {
        const total = (r.data.data || [])
          .filter(s => s.isPaid)
          .reduce((sum, s) => sum + (parseFloat(s.securityDeduction) || 0), 0);
        setRecoverSecurity(total);
      }).catch(() => {});
  }, [employee?._id]);

  const advanceBalance = liveEmp?.advanceBalance ?? (employee?.advanceBalance || 0);
  // securityMoney = total security deposited (from AddEmployee)
  const securityMoney  = liveEmp?.securityMoney   ?? (employee?.securityMoney  || 0);
  // monthlySecDed = fixed monthly deduction amount (from AddEmployee → Monthly Deduction field)
  const monthlySecDed  = liveEmp?.monthlyDeduction ?? (employee?.monthlyDeduction || 0);
  // remaining = securityMoney - already recovered
  const secRemaining   = Math.max(0, securityMoney - recoverSecurity);

  const base      = parseFloat(empMonthlySalary || employee?.monthlySalary || employee?.salary) || 0;
  const pfPct     = base > 0 && empPf  > 0 ? +((empPf  / base) * 100).toFixed(2) : 0;
  const esiPct    = base > 0 && empEsi > 0 ? +((empEsi / base) * 100).toFixed(2) : 0;
  const pfAmount  = (base * pfPct)  / 100;
  const esiAmount = (base * esiPct) / 100;

  // Always fixed 30 days for daily rate
  const FIXED_DAYS = 30;
  const dailyRate  = (empDailySalary || employee?.netDailySalary) || (base / FIXED_DAYS);
  const totalDays  = daysInMonth || FIXED_DAYS;

  // Editable absent days
  const [absentDays, setAbsentDays] = useState(
    passedAbsent !== undefined ? passedAbsent : (totalDays - (parseFloat(presentDays) || 0))
  );

  const absent  = parseFloat(absentDays) || 0;
  const present  = Math.min(Math.max(0, 30 - absent), 30);
  // Earned = monthly salary minus absent day deductions only
  // If absent = 0, employee gets full monthly salary regardless of actual month days
  // Use precise calculation: round((base - (absent * (base/30))) * 100) / 100
  const earned = Math.round((base - (absent * (base / 30))) * 100) / 100;

  const [otherDed,  setOtherDed]  = useState(existingOtherDeduction || 0);
  const [advAdj,    setAdvAdj]    = useState(existingAdvanceAdjust   || 0);
  const [returnSec, setReturnSec] = useState(false);
  const handleAdvChange = (val) => {
    const n = parseFloat(val) || 0;
    if (n > advanceBalance) setAdvAdj(advanceBalance);
    else if (n < 0)         setAdvAdj(0);
    else                    setAdvAdj(val);
  };

  // Security deduct this month:
  // - if employee left → 0 (we return remaining instead)
  // - if secRemaining <= 0 → fully recovered, deduct 0
  // - if secRemaining < monthlySecDed → deduct only what's remaining
  // - else deduct full monthlySecDed
  const secDeductAmt = returnSec ? 0
    : secRemaining <= 0 ? 0
    : Math.min(monthlySecDed, secRemaining);

  // When employee left: return whatever has already been recovered (deducted from past salaries)
  const secReturnAmt = returnSec ? recoverSecurity : 0;

  // Calculate net salary with proper rounding (round to 2 decimals at the end)
  const netSalaryRaw = earned
    - (parseFloat(otherDed) || 0)
    - (parseFloat(advAdj)   || 0)
    - secDeductAmt
    + secReturnAmt;
  const netSalary = Math.round(netSalaryRaw * 100) / 100; // Proper rounding to 2 decimals

  const monthLabel = selectedMonth
    ? new Date(selectedYear, selectedMonth - 1).toLocaleString('default', { month: 'long' })
    : '—';

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const adj = parseFloat(advAdj)   || 0;
      const oth = parseFloat(otherDed) || 0;
      await api.post('/salary', {
        employee:          employee?._id,
        college:           employee?.college?._id || employee?.college,
        month:             selectedMonth,
        year:              selectedYear,
        monthlySalary:     base,
        pfDeduction:       pfAmount,
        esiDeduction:      esiAmount,
        otherDeduction:    oth,
        advanceAdjust:     adj,
        presentDays:       present,
        absentDays:        absent,
        daysInMonth:       totalDays,
        earnedSalary:      earned,
        totalDeductions:   pfAmount + esiAmount + oth + adj + secDeductAmt,
        netSalary:         netSalary,
        securityDeduction: secDeductAmt,
        securityReturn:    secReturnAmt,
      });
      navigate(`${basePath}/salary`);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save salary.');
    }
  };

  return (
    <div className="create-salary-container">
      <div className="create-salary-header">
        <button className="create-salary-back-btn" type="button" onClick={() => navigate(`${basePath}/salary`)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="create-salary-title">Create Salary — {monthLabel} {selectedYear}</h2>
      </div>

      <form className="create-salary-form" onSubmit={handleSave}>

        {/* Employee Details */}
        <div className="create-salary-section">
          <h3 className="create-salary-section-title"><FaUser /> Employee Details</h3>
          <div className="create-salary-grid create-salary-grid-3">
            <div className="create-salary-input-group">
              <label className="create-salary-label">College</label>
              <div className="create-salary-input-wrapper">
                <FaBuilding className="create-salary-icon" />
                <input type="text" className="create-salary-input create-salary-readonly" value={collegeName || '—'} readOnly disabled />
              </div>
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Employee Name</label>
              <div className="create-salary-input-wrapper">
                <FaUser className="create-salary-icon" />
                <input type="text" className="create-salary-input create-salary-readonly" value={employee?.name || '—'} readOnly disabled />
              </div>
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Department</label>
              <div className="create-salary-input-wrapper">
                <FaBriefcase className="create-salary-icon" />
                <input type="text" className="create-salary-input create-salary-readonly" value={employee?.department?.name || '—'} readOnly disabled />
              </div>
            </div>
          </div>
        </div>

        {/* Salary Structure */}
        <div className="create-salary-section">
          <h3 className="create-salary-section-title"><FaMoneyBillWave /> Salary Structure</h3>
          <div className="create-salary-grid create-salary-grid-4">
            <div className="create-salary-input-group">
              <label className="create-salary-label">Monthly Salary (₹)</label>
              <input type="text" className="create-salary-input create-salary-readonly" value={`₹ ${base.toFixed(2)}`} readOnly disabled />
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">PF (%)</label>
              <div className="create-salary-input-wrapper">
                <FaPercentage className="create-salary-icon" />
                <input type="text" className="create-salary-input create-salary-readonly" value={`${pfPct}%`} readOnly disabled />
              </div>
              <span className="create-salary-hint-text">₹ {pfAmount.toFixed(2)}</span>
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">ESI (%)</label>
              <div className="create-salary-input-wrapper">
                <FaPercentage className="create-salary-icon" />
                <input type="text" className="create-salary-input create-salary-readonly" value={`${esiPct}%`} readOnly disabled />
              </div>
              <span className="create-salary-hint-text">₹ {esiAmount.toFixed(2)}</span>
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Daily Rate (₹)</label>
              <input type="text" className="create-salary-input create-salary-highlight-input" value={`₹ ${(Math.round(dailyRate * 100) / 100).toFixed(2)}`} readOnly disabled />
              <span className="create-salary-hint-text">Monthly ÷ 30 days (fixed)</span>
            </div>
          </div>
        </div>

        {/* Attendance & Adjustments */}
        <div className="create-salary-section">
          <h3 className="create-salary-section-title"><FaCalculator /> Attendance & Adjustments</h3>
          <div className="create-salary-grid create-salary-grid-4">
            <div className="create-salary-input-group">
              <label className="create-salary-label">Earned Salary (₹)</label>
              <input type="text" className="create-salary-input create-salary-highlight-input" value={`₹ ${earned.toFixed(2)}`} readOnly disabled />
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Other Deduction (₹)</label>
              <input type="number" className="create-salary-input" value={otherDed} onChange={e => setOtherDed(e.target.value)} min="0" />
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Advance Balance (₹)</label>
              <input type="text" className="create-salary-input create-salary-readonly" value={`₹ ${advanceBalance.toLocaleString()}`} readOnly disabled />
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Advance Adjustment (₹)</label>
              <input type="number" className="create-salary-input" value={advAdj}
                onChange={e => handleAdvChange(e.target.value)} min="0" max={advanceBalance} />
              {advanceBalance > 0 && <span className="create-salary-hint-text">Max: ₹ {advanceBalance}</span>}
            </div>
          </div>
        </div>

        {/* Security Money */}
        <div className="create-salary-section">
          <h3 className="create-salary-section-title"><FaShieldAlt /> Security Money</h3>
          <div className="create-salary-grid create-salary-grid-4">
            <div className="create-salary-input-group">
              <label className="create-salary-label">Security Money (₹)</label>
              <input type="text" className="create-salary-input create-salary-security-input"
                value={`₹ ${securityMoney.toLocaleString()}`} readOnly disabled />
              <span className="create-salary-hint-text">Total security deposited</span>
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Recover Security (₹)</label>
              <input type="text" className="create-salary-input create-salary-security-input"
                value={`₹ ${recoverSecurity.toLocaleString()}`} readOnly disabled />
              <span className="create-salary-hint-text">Already recovered so far</span>
            </div>
            <div className="create-salary-input-group">
              <label className="create-salary-label">Security Deduct This Month (₹)</label>
              <input
                type="text"
                className={`create-salary-input ${secDeductAmt === 0 ? 'create-salary-readonly' : 'create-salary-deduct-input'}`}
                value={secDeductAmt === 0
                  ? (secRemaining <= 0 ? '₹ 0 (Fully Recovered)' : '₹ 0')
                  : `- ₹ ${secDeductAmt.toFixed(2)}`}
                readOnly disabled
              />
              {secDeductAmt > 0 && <span className="create-salary-hint-text">Monthly deduction: ₹{monthlySecDed.toLocaleString()}</span>}
            </div>
            <div className="create-salary-input-group create-salary-return-group">
              <label className="create-salary-label">Employee Left? Return Security</label>
              <label className="create-salary-toggle-label">
                <input
                  type="checkbox"
                  checked={returnSec}
                  onChange={e => setReturnSec(e.target.checked)}
                  disabled={recoverSecurity <= 0}
                />
                <span className="create-salary-toggle-text">
                  {returnSec
                    ? `Return ₹ ${recoverSecurity.toLocaleString()} (recovered) to employee`
                    : 'No — keep security'}
                </span>
              </label>
              {returnSec && <span className="create-salary-hint-text create-salary-hint-green">+ ₹ {recoverSecurity.toLocaleString()} added to net salary</span>}
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="create-salary-final-container">
          <div className="create-salary-receipt-box">
            <h4 className="create-salary-receipt-title">Salary Summary</h4>
            <div className="create-salary-receipt-row">
              <span className="create-salary-receipt-label">Earned Salary</span>
              <span className="create-salary-receipt-val">₹ {earned.toFixed(2)}</span>
            </div>
            {(parseFloat(otherDed) || 0) > 0 && (
              <div className="create-salary-receipt-row create-salary-receipt-deduct">
                <span className="create-salary-receipt-label">Less: Other Deductions</span>
                <span className="create-salary-receipt-val">- ₹ {(parseFloat(otherDed) || 0).toFixed(2)}</span>
              </div>
            )}
            {(parseFloat(advAdj) || 0) > 0 && (
              <div className="create-salary-receipt-row create-salary-receipt-deduct">
                <span className="create-salary-receipt-label">Less: Advance Adjustment</span>
                <span className="create-salary-receipt-val">- ₹ {(parseFloat(advAdj) || 0).toFixed(2)}</span>
              </div>
            )}
            {secDeductAmt > 0 && (
              <div className="create-salary-receipt-row create-salary-receipt-deduct">
                <span className="create-salary-receipt-label">Less: Security Deduction</span>
                <span className="create-salary-receipt-val">- ₹ {secDeductAmt.toFixed(2)}</span>
              </div>
            )}
            {secReturnAmt > 0 && (
              <div className="create-salary-receipt-row create-salary-receipt-add">
                <span className="create-salary-receipt-label">Add: Security Return (Employee Left) <small>(recovered ₹{recoverSecurity.toLocaleString()} returned)</small></span>
                <span className="create-salary-receipt-val">+ ₹ {secReturnAmt.toFixed(2)}</span>
              </div>
            )}
            <div className="create-salary-receipt-divider" />
            <div className="create-salary-receipt-row create-salary-receipt-total">
              <span className="create-salary-receipt-label">Net Payable</span>
              <span className="create-salary-receipt-val">₹ {netSalary.toFixed(2)}</span>
            </div>
          </div>
          <div className="create-salary-action-box">
            <button type="submit" className="create-salary-save-btn">
              <FaSave /> Save Salary
            </button>
          </div>
        </div>

      </form>
    </div>
  );
};

export default CreateSalary;
